import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { 
  ArrowLeft, Calendar, Ruler, Phone, CheckCircle2, MapPin, Info, Edit3, Warehouse 
} from 'lucide-react';

const ShortTermStorageForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // n8n प्रोडक्शन URL
  const n8nUrl = "http://localhost:5678/webhook/apni-manzil-logistics";

  
  const [formData, setFormData] = useState({
    location: '', storageDuration: '', spaceNeeded: '',
    materialType: 'General Goods', otherMaterial: '', 
    mobile: '', additionalNotes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // मोबाईल नंबर व्हॅलिडेट करा
    if (!formData.mobile || formData.mobile.length < 10) {
      alert("कृपया १० अंकी वैध मोबाईल नंबर भरा!");
      return;
    }

    setLoading(true);
    const finalMaterial = formData.materialType === "Other" ? formData.otherMaterial : formData.materialType;
    
    // पूर्ण डेटा एकत्र केला
    const bookingData = {
      ...formData,
      mobile: `+91${formData.mobile}`,
      materialType: finalMaterial,
      serviceType: "Short Term Storage",
      status: "Pending",
      timestamp: new Date().toISOString()
    };

    try {
      // १. Firebase मध्ये सेव्ह करा
      await addDoc(collection(db, "warehouse_requests"), bookingData);

      // २. n8n कडे डेटा पाठवा
      await fetch(n8nUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData),
      });

      alert("Storage inquiry submitted successfully!");
      navigate(-1);
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      {/* 🟦 Header */}
      <div className="bg-[#002D5E] text-white p-6 flex items-center gap-4 sticky top-[72px] lg:top-[108px] z-30 shadow-md">
        <button onClick={() => navigate(-1)} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition">
          <ArrowLeft size={20}/>
        </button>
        <h1 className="text-xl font-black uppercase tracking-tighter">Short Term <span className="text-orange-500">Storage</span></h1>
      </div>

      <div className="max-w-xl mx-auto p-4">
        <div className="mt-4 mb-6 relative overflow-hidden rounded-[2.5rem] shadow-xl border-4 border-white">
          <img 
            src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&fit=crop&q=80&w=1000" 
            alt="Modern Warehouse" 
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
            <div className="flex items-center gap-2 text-white">
              <Warehouse size={20} className="text-orange-400" />
              <span className="font-bold text-sm uppercase tracking-widest">Safe Storage Solutions</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100 space-y-4">
            <h2 className="text-[10px] font-black uppercase text-blue-700 tracking-[0.2em] ml-2">Fill Details Below</h2>

            <input name="location" placeholder="City / Area" required className="form-input" onChange={handleChange} />
            <input type="number" name="spaceNeeded" placeholder="Space Required (Sq. Ft.)" required className="form-input" onChange={handleChange} />
            <input name="storageDuration" placeholder="Duration (e.g. 15 Days)" required className="form-input" onChange={handleChange} />

            <div className="space-y-2">
              <select name="materialType" className="form-input font-bold" onChange={handleChange} value={formData.materialType}>
                <option value="General Goods">General Goods</option>
                <option value="Household Items">Household Items</option>
                <option value="Industrial Material">Industrial Material</option>
                <option value="Agriculture Products">Agriculture Products</option>
                <option value="Other">Other (Please specify)</option>
              </select>
            </div>

            {formData.materialType === "Other" && (
              <div className="relative animate-in slide-in-from-top-2 duration-300">
                <Edit3 size={18} className="absolute left-4 top-4 text-orange-500" />
                <input name="otherMaterial" placeholder="Specify material type" required className="form-input pl-12 border-orange-200 bg-orange-50/30" onChange={handleChange} />
              </div>
            )}

            <textarea name="additionalNotes" placeholder="Special requirements (CCTV, Pallets, etc.)" rows="3" className="form-input pt-4" onChange={handleChange}></textarea>
            
            {/* Mobile Number with +91 Prefix */}
            <div className="flex items-center bg-[#f8fafc] rounded-[1.25rem] overflow-hidden border-2 border-[#f1f5f9] focus-within:border-[#002D5E] focus-within:bg-white transition-all">
              <span className="bg-slate-200 px-4 py-4 text-slate-700 font-extrabold border-r border-slate-300 select-none text-sm">
                +91
              </span>
              <input 
                type="tel" 
                name="mobile" 
                placeholder="Mobile Number" 
                required 
                maxLength="10" 
                value={formData.mobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({ ...formData, mobile: val });
                }}
                className="w-full p-4 bg-transparent border-none outline-none text-sm font-black tracking-[2px] text-slate-900" 
              />
            </div>
            
            <button 
              disabled={loading} 
              className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl transition-all ${loading ? 'bg-slate-400' : 'bg-[#002D5E] text-white hover:bg-blue-900 active:scale-95'}`}
            >
              {loading ? "Submitting..." : "Get Free Quote"}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .form-input {
          width: 100%;
          padding: 1rem 1.25rem;
          background: #f8fafc;
          border-radius: 1.25rem;
          border: 2px solid #f1f5f9;
          font-weight: 700;
          outline: none;
          transition: all 0.2s;
          font-size: 0.9rem;
        }
        .form-input:focus {
          border-color: #002D5E;
          background: #fff;
        }
      `}</style>
    </div>
  );
};

export default ShortTermStorageForm;